import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Calculator, ArrowLeft, ArrowRight, Laptop, HardDrive, Monitor, Cpu, Fan, Wrench, Zap, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { laptopRepairData } from '../mock';
import HeaderSocials from '../components/HeaderSocials';

const LaptopsPage = () => {
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedRepair, setSelectedRepair] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [repairData, setRepairData] = useState(laptopRepairData);
  const [seo, setSeo] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calculator-data/laptop`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && Object.keys(d).length) setRepairData(d); })
      .catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/page-content/laptops`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.content && Object.keys(d.content).length) setSeo(d.content); })
      .catch(() => {});
  }, []);

  const brands = Object.keys(repairData);
  const models = selectedBrand ? repairData[selectedBrand].models : [];
  const repairs = selectedBrand ? Object.keys(repairData[selectedBrand].repairs) : [];

  const handleCalculate = () => {
    if (selectedBrand && selectedModel && selectedRepair) {
      const price = repairData[selectedBrand].repairs[selectedRepair][selectedModel];
      setCalculatedPrice(price);
    }
  };

  const handleReset = () => {
    setSelectedBrand('');
    setSelectedModel('');
    setSelectedRepair('');
    setCalculatedPrice(null);
  };

  const laptopServices = [
    {
      icon: Monitor,
      title: 'Заміна екрану',
      description: 'Заміна розбитого або пошкодженого дисплея ноутбука',
      details: [
        'Оригінальні та якісні аналоги матриць',
        'Ремонт від 1 до 3 днів',
        'Гарантія на матрицю',
        'Калібрування кольорів'
      ],
      link: '/laptops/screen-repair'
    },
    {
      icon: HardDrive,
      title: 'Заміна HDD/SSD',
      description: 'Установка нового жорсткого диска або SSD',
      details: [
        'Перенесення даних з старого диску',
        'Установка операційної системи',
        'Оптимізація швидкості роботи',
        'Збереження всіх файлів'
      ],
      link: '/laptops/hdd-repair'
    },
    {
      icon: Cpu,
      title: 'Ремонт материнської плати',
      description: 'Складний ремонт системної плати',
      details: [
        'Мікросхемний ремонт',
        'Заміна пошкоджених мікросхем',
        'Відновлення доріжок',
        'Професійне обладнання'
      ],
      link: '#calculator'
    },
    {
      icon: Fan,
      title: 'Чистка ноутбуків',
      description: 'Чистка від пилу, заміна термопасти',
      details: [
        'Повна чистка від пилу',
        'Заміна термопасти',
        'Налаштування вентиляторів',
        'Зниження температури'
      ],
      link: '/laptop-cleaning'
    },
    {
      icon: Zap,
      title: 'Ремонт живлення',
      description: 'Заміна роз\'єму живлення, ремонт зарядки',
      details: [
        'Заміна роз\'єму живлення',
        'Ремонт контролерів живлення',
        'Перевірка акумулятора',
        'Заміна адаптера живлення'
      ],
      link: '#calculator'
    },
    {
      icon: Wrench,
      title: 'Апгрейд ноутбука',
      description: 'Збільшення RAM, установка SSD',
      details: [
        'Збільшення оперативної пам\'яті',
        'Установка SSD замість HDD',
        'Заміна процесора (якщо можливо)',
        'Підвищення продуктивності'
      ],
      link: '#calculator'
    },
    {
      icon: Zap,
      title: 'Прискорення ноутбука',
      description: 'Комплексний апгрейд для максимальної продуктивності',
      details: [
        'Установка SSD + збільшення RAM',
        'Оптимізація системи',
        'Чистка та заміна термопасти',
        'Швидкість роботи як у нового'
      ],
      link: '#calculator'
    }
  ];

  return (
    <div className="homepage">
      <Helmet>
        <title>{(seo && seo.metaTitle) || 'Ремонт ноутбуків в Одесі - CIMM | MacBook, Dell, HP, Lenovo'}</title>
        <meta name="description" content={(seo && seo.metaDescription) || "Професійний ремонт ноутбуків MacBook, Dell, HP, Lenovo в Одесі. Заміна екрану, чистка, апгрейд. Безкоштовна діагностика. Гарантія на роботи."} />
        <meta name="keywords" content={(seo && seo.metaKeywords) || "ремонт ноутбуків одеса, ремонт macbook одеса, чистка ноутбука, заміна екрану ноутбука, апгрейд ноутбука"} />
        <link rel="canonical" href="https://cimm.od.ua/laptops" />
        <meta property="og:title" content="Ремонт ноутбуків в Одесі - CIMM" />
        <meta property="og:description" content="Професійний ремонт ноутбуків всіх брендів. Безкоштовна діагностика." />
      </Helmet>
      {/* Header */}
      <header className="cyber-header fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="text-2xl font-bold neon-text">CIMM</div>
            <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Головна</Link>
            <Link to="/phones" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Телефони</Link>
            <Link to="/laptops" className="text-orange-500">Ноутбуки</Link>
            <Link to="/laptop-cleaning" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Чистка</Link>
            <Link to="/contacts" className="text-gray-300 hover:text-orange-500 transition-all duration-300">Контакти</Link>
          </nav>
          <div className="flex items-center gap-2">
            <HeaderSocials />
            <a href="tel:+380974178788" className="neon-text font-semibold hover:drop-shadow-[0_0_15px_rgba(255,140,0,0.8)] transition-all duration-300" data-testid="header-phone-link">
              +38 097 417 8788
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <section className="pt-32 pb-20 px-4 dark-section min-h-screen">
        <div className="container mx-auto max-w-6xl relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-orange-500 mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Повернутися на головну
          </Link>

          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Ремонт <span className="neon-text">ноутбуків</span> в Одесі
            </h1>
            <p className="text-xl text-gray-400">
              Професійний ремонт MacBook, Dell, HP, Lenovo, Asus та інших брендів
            </p>
          </div>

          {/* Services Info */}
          <div className="mb-16" id="services">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Наші <span className="neon-text">послуги</span></h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {laptopServices.map((service, index) => {
                const IconComponent = service.icon;
                
                const CardContent = () => (
                  <>
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="w-7 h-7 text-orange-500 icon-glow" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                    <p className="text-gray-400 mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {service.details.map((detail, idx) => (
                        <li key={idx} className="text-sm text-gray-300 flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                    {(
                      <div className="mt-4 flex items-center gap-2 text-orange-500 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-sm font-semibold">Детальніше</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    )}
                  </>
                );
                
                return (
                  <Link 
                    key={index}
                    to={service.link}
                    className="glass-card p-6 hover:scale-105 transition-transform duration-300 block group cursor-pointer"
                  >
                    <CardContent />
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="mb-16">
            <div className="glass-card p-8">
              <h2 className="text-2xl font-bold text-white mb-6 text-center">Чому обирають нас?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white mb-1">Досвід понад 10 років</h3>
                    <p className="text-gray-400 text-sm">Ремонтуємо ноутбуки всіх брендів та моделей</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white mb-1">Оригінальні запчастини</h3>
                    <p className="text-gray-400 text-sm">Використовуємо тільки якісні комплектуючі з гарантією</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white mb-1">Безкоштовна діагностика</h3>
                    <p className="text-gray-400 text-sm">Визначимо проблему та назвемо точну ціну ремонту</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white mb-1">Гарантія на роботи</h3>
                    <p className="text-gray-400 text-sm">Надаємо гарантію на всі види ремонту</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Calculator */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              <span className="neon-text">Калькулятор</span> вартості ремонту
            </h2>

            <div className="glass-card p-8">
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Brand Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Виробник
                </label>
                <Select value={selectedBrand} onValueChange={(value) => {
                  setSelectedBrand(value);
                  setSelectedModel('');
                  setSelectedRepair('');
                  setCalculatedPrice(null);
                }}>
                  <SelectTrigger className="calc-select bg-white/5 border-orange-500/30">
                    <SelectValue placeholder="Оберіть виробника" />
                  </SelectTrigger>
                  <SelectContent className="calc-dropdown">
                    {brands.map((brand) => (
                      <SelectItem key={brand} value={brand} className="calc-dropdown-item">
                        {brand}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Model Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Модель
                </label>
                <Select 
                  value={selectedModel} 
                  onValueChange={(value) => {
                    setSelectedModel(value);
                    setCalculatedPrice(null);
                  }}
                  disabled={!selectedBrand}
                >
                  <SelectTrigger className="calc-select bg-white/5 border-orange-500/30 disabled:opacity-50">
                    <SelectValue placeholder="Оберіть модель" />
                  </SelectTrigger>
                  <SelectContent className="calc-dropdown max-h-[300px]">
                    {models.map((model) => (
                      <SelectItem key={model} value={model} className="calc-dropdown-item">
                        {model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Repair Type Selection */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Тип ремонту
                </label>
                <Select 
                  value={selectedRepair} 
                  onValueChange={(value) => {
                    setSelectedRepair(value);
                    setCalculatedPrice(null);
                  }}
                  disabled={!selectedBrand}
                >
                  <SelectTrigger className="calc-select bg-white/5 border-orange-500/30 disabled:opacity-50">
                    <SelectValue placeholder="Оберіть тип ремонту" />
                  </SelectTrigger>
                  <SelectContent className="calc-dropdown">
                    {repairs.map((repair) => (
                      <SelectItem key={repair} value={repair} className="calc-dropdown-item">
                        {repair}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Calculate Button */}
            <div className="flex gap-4">
              <Button
                onClick={handleCalculate}
                disabled={!selectedBrand || !selectedModel || !selectedRepair}
                className="flex-1 futuristic-btn bg-orange-500 hover:bg-orange-600 text-white border-0 disabled:opacity-50"
                size="lg"
              >
                <Calculator className="w-5 h-5 mr-2" />
                Розрахувати вартість
              </Button>
              <Button
                onClick={handleReset}
                variant="outline"
                className="neon-border border-orange-500 text-orange-500 hover:bg-orange-500/10 bg-transparent"
                size="lg"
              >
                Скинути
              </Button>
            </div>

            {/* Price Display */}
            {calculatedPrice && (
              <div className="mt-8 p-6 bg-gray-900/50 rounded-xl border border-orange-500/30 animate-fade-in-up">
                <div className="text-center">
                  <p className="text-gray-300 text-lg mb-2">Орієнтовна вартість ремонту:</p>
                  <p className="text-5xl font-bold neon-text">{calculatedPrice} грн</p>
                  <p className="text-gray-400 text-sm mt-4">
                    * Точна вартість визначається після безкоштовної діагностики
                  </p>
                </div>
              </div>
            )}

            {/* Info Block */}
            <div className="mt-6 p-4 bg-gray-900/30 rounded-lg border border-orange-500/20">
              <p className="text-gray-400 text-sm text-center">
                <span className="text-orange-500 font-semibold">Увага:</span> Вказані ціни є орієнтовними.
                Остаточна вартість ремонту визначається майстром після діагностики пристрою.
              </p>
            </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LaptopsPage;